import { chakra } from '@chakra-ui/react';

import type { PaginationParams } from './types';

import { useI18n } from 'lib/i18n/context';
import PaginationControl from 'ui/shared/pagination/PaginationControl';

interface Props extends PaginationParams {
  className?: string;
  variant?: 'default' | 'explorer';
}

const Pagination = ({
  page,
  onNextPageClick,
  onPrevPageClick,
  resetPage,
  hasPages,
  hasNextPage,
  className,
  canGoBackwards,
  isLoading,
  isVisible,
  totalPages,
  canGoToLast,
  onLastPageClick,
  onPageJump,
  variant = 'default',
}: Props) => {
  const { t } = useI18n();

  if (!isVisible) {
    return null;
  }

  const showSkeleton = page === 1 && !hasPages && isLoading;

  return (
    <PaginationControl
      className={ className }
      currentPage={ page }
      totalPages={ totalPages }
      variant={ variant }
      isLoading={ isLoading }
      isVisible={ isVisible }
      showSkeleton={ showSkeleton }
      isPageEditable={ Boolean(onPageJump) }
      onPageSubmit={ onPageJump }
      firstAction={{
        label: t('shared.pagination.actions.first'),
        onClick: resetPage,
        isDisabled: page === 1 || isLoading,
      }}
      prevAction={{
        ariaLabel: t('shared.pagination.aria.prevPage'),
        onClick: onPrevPageClick,
        isDisabled: !canGoBackwards || isLoading,
        direction: 'left',
      }}
      nextAction={{
        ariaLabel: t('shared.pagination.aria.nextPage'),
        onClick: onNextPageClick,
        isDisabled: !hasNextPage || isLoading,
        direction: 'right',
      }}
      lastAction={{
        label: t('shared.pagination.actions.last'),
        onClick: onLastPageClick,
        isDisabled: !canGoToLast || isLoading,
      }}
    />
  );
};

export default chakra(Pagination);
